import React from 'react'
import { useBackgroundPlacementActionsOptional } from '@/contexts/background-placement-context'
import { useCanvasPanState } from '../use-canvas-pan-state'
import { useCheckpointLoad } from '../use-checkpoint-load'
import { useContextMenus } from '../use-context-menus'
import { useDefaultFontSeed } from '../use-default-font-seed'
import { useGuideSelection } from '../use-guide-selection'
import { useGuidesState } from '../use-guides-state'
import { useLayerArrangeGates } from '../use-layer-arrange-gates'
import { useLinkedChainsRef } from '../use-linked-chains-ref'
import { usePhotoFillNaturalSize } from '../use-photo-fill-natural-size'
import { useShapeManagerRef } from '../use-shape-manager-ref'
import { useShapeTextEditEntry } from '../use-shape-text-edit-entry'
import { useSmartGuidesState } from '../use-smart-guides-state'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * What every later group builds on: the ShapeManager handle and the refs mirroring layer order and
 * text chains, the canvas DOM refs, the default text style for new frames, and the shared
 * interaction state (smart guides, context menus, shape-text editing, panning, ruler guides). Also
 * hands checkpoint shapes to ShapeManager when a spread loads.
 */
export function useCanvasFoundation({
  defaultFontFamilyId,
  defaultFontName,
  defaultFontSize,
  newTextBoxKerning,
  newTextBoxTracking,
  newTextBoxUnderline,
  newTextBoxAlignment,
  shapeManagerRef,
  unifiedLayerIds,
  linkedTextChains,
  showGuides,
  showSmartGuides,
  isPanModeActive,
  checkpointShapes,
  checkpointLoadKey,
  onCheckpointTextBoxesApplied,
  guides: externalGuides,
  onGuidesChange,
}: ResolvedCenterCanvasConfig) {
  const { resolvedDefaultFontFamilyId, resolvedDefaultFontId, newShapeTextSeedStyle } =
    useDefaultFontSeed({
      defaultFontFamilyId,
      defaultFontName,
      defaultFontSize,
      newTextBoxKerning,
      newTextBoxTracking,
      newTextBoxUnderline,
      newTextBoxAlignment,
    })
  // Optional, like the fonts context in `use-default-font-seed`: this component renders in tests
  // without a provider. Drives the canvas menu's "Adjust background" item.
  const backgroundPlacement = useBackgroundPlacementActionsOptional()

  const { internalShapeManagerRef, setShapeManagerRef } = useShapeManagerRef({ shapeManagerRef })
  const {
    unifiedLayerIdsRef,
    canUnifiedBringForward,
    canUnifiedSendBackward,
    getArrangeSelectedIds,
  } = useLayerArrangeGates({ unifiedLayerIds, internalShapeManagerRef })
  const { linkedTextChainsRef, getLinkedTextChainsForShapeManager } = useLinkedChainsRef({
    linkedTextChains,
  })

  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const pasteboardRef = React.useRef<HTMLDivElement | null>(null)
  /** Bleed rect (same coords as DoubleSpreadDocument `left`/`top`); not the outer centering wrapper. */
  const documentCanvasRef = React.useRef<HTMLDivElement | null>(null)

  const { activeSmartGuides, setActiveSmartGuides, effectiveSmartGuides } = useSmartGuidesState({
    showGuides,
    showSmartGuides,
  })

  const contextMenus = useContextMenus({ internalShapeManagerRef, linkedTextChains })

  /** Guides sit at z-index 4 above text (z-index 3); disable guide hit-testing while editing so click-drag can select. */
  // TextManager no longer renders (Phase 2), so this can never flip true — kept as a named
  // constant rather than inlining `false` at each read site below.
  const isInlineTextEditing = false
  const {
    isShapeTextEditing,
    setIsShapeTextEditing,
    isPointerOnEditingShapeText,
    tryEnterShapeTextEditFromPointer,
  } = useShapeTextEditEntry({ internalShapeManagerRef, newShapeTextSeedStyle })

  const {
    setIsSpacePanActive,
    isDragPanning,
    setIsDragPanning,
    isPanInteractionActive,
    panDragRef,
    panFrameRef,
    panScrollTargetRef,
  } = useCanvasPanState({ isPanModeActive })

  useCheckpointLoad({
    internalShapeManagerRef,
    checkpointShapes,
    checkpointLoadKey,
    onCheckpointTextBoxesApplied,
  })

  usePhotoFillNaturalSize({ internalShapeManagerRef, checkpointShapes, checkpointLoadKey })

  const {
    guides,
    updateGuides,
    guidesRef,
    localDragGuides,
    setLocalDragGuides,
    localDragGuidesRef,
    updateGuidesRef,
  } = useGuidesState({ externalGuides, onGuidesChange })
  const {
    selectedGuideIds,
    setSelectedGuideIds,
    selectedGuideIdsRef,
    lastShapeSelectionClearKeyRef,
  } = useGuideSelection()

  return {
    resolvedDefaultFontFamilyId,
    resolvedDefaultFontId,
    newShapeTextSeedStyle,
    internalShapeManagerRef,
    setShapeManagerRef,
    unifiedLayerIdsRef,
    canUnifiedBringForward,
    canUnifiedSendBackward,
    getArrangeSelectedIds,
    linkedTextChainsRef,
    getLinkedTextChainsForShapeManager,
    activeSmartGuides,
    setActiveSmartGuides,
    effectiveSmartGuides,
    isShapeTextEditing,
    setIsShapeTextEditing,
    isPointerOnEditingShapeText,
    tryEnterShapeTextEditFromPointer,
    setIsSpacePanActive,
    isDragPanning,
    setIsDragPanning,
    isPanInteractionActive,
    panDragRef,
    panFrameRef,
    panScrollTargetRef,
    guides,
    updateGuides,
    guidesRef,
    localDragGuides,
    setLocalDragGuides,
    localDragGuidesRef,
    updateGuidesRef,
    selectedGuideIds,
    setSelectedGuideIds,
    selectedGuideIdsRef,
    lastShapeSelectionClearKeyRef,
    backgroundPlacement,
    scrollRef,
    pasteboardRef,
    documentCanvasRef,
    contextMenus,
    isInlineTextEditing,
  }
}
